/**
 * EntityFormAlert — form-level feedback sub-component.
 *
 * Renders an Alert when the form has a submission error (`form.formError`)
 * or a successful submission (`form.isSubmitSuccessful`).
 * When neither condition is active the component renders nothing.
 *
 * The underlying Alert base component already includes `role="alert"`,
 * ensuring screen readers announce state changes immediately.
 *
 * @example
 * ```tsx
 * <EntityForm config={cfg}>
 *   <EntityForm.Alert />
 *   <EntityForm.Fields />
 *   <EntityForm.Actions />
 * </EntityForm>
 * ```
 */

import * as React from 'react';
import { Alert } from '@components/base/feedback/Alert/Alert';
import { useEntityFormContext } from '../context/EntityFormContext';
import type { EntityFormAlertProps } from '../types';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Displays a form-level success or error alert.
 *
 * - Error state: shown when `form.formError` is a non-empty string.
 * - Success state: shown when `form.isSubmitSuccessful` is `true`.
 * - Otherwise: renders `null`.
 *
 * Accepts an optional `className` prop that is merged with the
 * `config.cssOverrides?.alert` class from the form configuration.
 */
const EntityFormAlert: React.FC<EntityFormAlertProps> = ({ className }) => {
  const { form, config } = useEntityFormContext();

  // ---- Determine alert state ------------------------------------------------

  const hasError = Boolean(form.formError);
  const hasSuccess = !hasError && form.isSubmitSuccessful;

  if (!hasError && !hasSuccess) {
    return null;
  }

  // ---- Resolve classes ------------------------------------------------------

  const overrideClass = config.cssOverrides?.alert ?? '';
  const combinedClassName = [overrideClass, className]
    .filter(Boolean)
    .join(' ') || undefined;

  // ---- Render ---------------------------------------------------------------

  if (hasError) {
    return (
      <Alert
        variant="error"
        title="Error"
        className={combinedClassName}
      >
        {form.formError}
      </Alert>
    );
  }

  return (
    <Alert
      variant="success"
      title="Success"
      className={combinedClassName}
    >
      Form submitted successfully.
    </Alert>
  );
};

EntityFormAlert.displayName = 'EntityFormAlert';

export { EntityFormAlert };
